import { CreateTransportOrder as CreateTransportOrderSchema } from 'api/generated/transportOrders.zod';
import type { CreateTransportOrder, TransportOrderDetail } from 'api/generated/transportOrdersApi';

import type { DateTimeRange } from './DateTimeRangeField';

export type TransportOrderFormType = {
    launchHubId: string;
    landingHubId: string;
    loadId: string;
    vin: string;
    pickupWindow: DateTimeRange;
    deliveryWindow: DateTimeRange;
};

const emptyRange = (): DateTimeRange => ({ from: '', to: '' });

export const emptyFormValues = (): TransportOrderFormType => ({
    launchHubId: '',
    landingHubId: '',
    loadId: '',
    vin: '',
    pickupWindow: emptyRange(),
    deliveryWindow: emptyRange(),
});

const toFormValues = (transportOrder: TransportOrderDetail): TransportOrderFormType => ({
    launchHubId: transportOrder.launchHub.id,
    landingHubId: transportOrder.landingHub.id,
    loadId: transportOrder.loadId,
    vin: transportOrder.vin,
    pickupWindow: { from: transportOrder.pickupWindow.from, to: transportOrder.pickupWindow.to },
    deliveryWindow: { from: transportOrder.deliveryWindow.from, to: transportOrder.deliveryWindow.to },
});

export const initialFormValues = (transportOrder: TransportOrderDetail | undefined): TransportOrderFormType =>
    transportOrder ? toFormValues(transportOrder) : emptyFormValues();

export const toCreatePayload = (values: TransportOrderFormType): CreateTransportOrder => ({
    launchHubId: values.launchHubId,
    landingHubId: values.landingHubId,
    loadId: values.loadId,
    vin: values.vin,
    pickupWindow: { from: values.pickupWindow.from, to: values.pickupWindow.to },
    deliveryWindow: { from: values.deliveryWindow.from, to: values.deliveryWindow.to },
});

export const isRangeComplete = (range: DateTimeRange): boolean => range.from !== '' && range.to !== '';

const isRangeInFuture = (range: DateTimeRange, now: number): boolean =>
    [range.from, range.to].every(iso => iso === '' || new Date(iso).getTime() >= now);

const isRangeOrdered = (range: DateTimeRange): boolean =>
    !isRangeComplete(range) || Date.parse(range.from) < Date.parse(range.to);

const isDeliveryAfterPickup = (values: TransportOrderFormType): boolean =>
    !isRangeComplete(values.pickupWindow) ||
    !isRangeComplete(values.deliveryWindow) ||
    Date.parse(values.pickupWindow.to) <= Date.parse(values.deliveryWindow.from);

export const areTransportWindowsOrdered = (values: TransportOrderFormType): boolean =>
    isRangeOrdered(values.pickupWindow) && isRangeOrdered(values.deliveryWindow) && isDeliveryAfterPickup(values);

export const areTransportWindowsInFuture = (values: TransportOrderFormType, now: number = Date.now()): boolean =>
    isRangeInFuture(values.pickupWindow, now) && isRangeInFuture(values.deliveryWindow, now);

export const isFormValid = (values: TransportOrderFormType, now: number = Date.now()): boolean =>
    values.loadId.trim() !== '' &&
    areTransportWindowsInFuture(values, now) &&
    areTransportWindowsOrdered(values) &&
    CreateTransportOrderSchema.safeParse(toCreatePayload(values)).success;
